// WithdrawalReceipt.jsx — Refund Receipt
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FaCheckCircle, FaClock, FaTimesCircle, FaArrowLeft } from "react-icons/fa";
import DashboardNavbar from "../components/layout/DashboardNavbar";

export function WithdrawalReceipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, data, withdrawalDetails, walletBalance } = location.state || {};
  const REFUND_FEE = 200;

  if (!status || !withdrawalDetails) { navigate("/withdraw"); return null; }

  const amount = parseFloat(withdrawalDetails.amount);
  const totalDebit = amount + REFUND_FEE;
  const currentDate = new Date().toLocaleDateString("en-GB");
  const currentTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const isSuccess = status === "completed" || status === "success";
  const isPending = status === "pending";
  const isFailed = status === "failed";

  const StatusIcon = isSuccess ? FaCheckCircle : isPending ? FaClock : FaTimesCircle;
  const statusColor = isSuccess ? "text-green-500" : isPending ? "text-yellow-500" : "text-red-500";
  const statusBg = isSuccess ? "bg-green-50 border-green-200" : isPending ? "bg-yellow-50 border-yellow-200" : "bg-red-50 border-red-200";
  const statusLabel = isSuccess ? "Completed" : isPending ? "Pending Approval" : "Failed";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DashboardNavbar />
      <main className="w-full max-w-md mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate("/dashboard")} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 shadow-sm">
            <FaArrowLeft className="text-gray-600 w-4 h-4" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Refund Receipt</h1>
        </div>

        <div className={`border rounded-2xl p-6 mb-6 text-center ${statusBg}`}>
          <StatusIcon className={`w-12 h-12 mx-auto mb-3 ${statusColor}`} />
          <p className={`text-xl font-bold ${statusColor}`}>{statusLabel}</p>
          {isPending && <p className="text-sm text-gray-600 mt-2">Your refund request is awaiting Super Admin approval.</p>}
          {isFailed && <p className="text-sm text-red-600 mt-2">{data?.message || "Refund request could not be processed."}</p>}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
          <h3 className="font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100">Transaction Details</h3>
          <div className="space-y-3 text-sm">
            {[
              ["Reference", data?.reference || data?.id || "—"],
              ["Date & Time", `${currentDate} at ${currentTime}`],
              ["Refund Amount", `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`],
              ["Processing Fee", `₦${REFUND_FEE.toLocaleString()}`],
              ["Total Deducted", `₦${totalDebit.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <span className="text-gray-500">{label}</span>
                <span className="font-semibold text-gray-900 text-right max-w-[60%] break-all">{value}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Bank Details</h4>
            <div className="space-y-2 text-sm">
              {[
                ["Bank", withdrawalDetails.recipient_bank_name],
                ["Account Number", withdrawalDetails.recipient_account_number],
                ["Account Name", withdrawalDetails.recipient_account_name],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-semibold text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button onClick={() => navigate("/dashboard")} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl h-12 font-semibold">
            Back to Dashboard
          </Button>
          {isFailed && (
            <Button onClick={() => navigate("/withdraw")} variant="outline" className="w-full rounded-xl h-12">Try Again</Button>
          )}
        </div>
      </main>
    </div>
  );
}
